import { z } from 'zod';
/**
 * Time-Tracking (Feature-Flag-aktiviert).
 * Erfasst Arbeitszeit pro User+Projekt für interne Auslastung oder
 * Rechnungsentwurf.
 */
// ============================================================
// Positionen / Leistungsarten
// ============================================================
/**
 * Positionen (Leistungsarten), auf die Stunden gebucht werden.
 *
 * WARUM ein Enum statt Freitext: die bisherige Freitext-`category` konnte
 * nicht ausgewertet werden — „Vor Ort", „vor-ort" und „Ortstermin" waren drei
 * verschiedene Töpfe. Auswertung, Filter und Stundensatz-Zuordnung brauchen
 * einen stabilen Schlüssel. Die Schlüssel sind bewusst technisch (snake_case,
 * stabil), die Anzeige läuft ausschließlich über `TIME_ACTIVITY_META` — EINE
 * Quelle für Backend, Frontend und CSV.
 *
 * Herleitung des Katalogs: die fünf Ansätze aus dem Demo-Seed (Vor-Ort,
 * Planung, Korrespondenz, Prüfung, Dokumentation) waren zu grob, um die Frage
 * „wohin ist der Aufwand geflossen" zu beantworten — „Planung" umfasste sowohl
 * Konzepterstellung als auch Nachweisführung, die kaufmännisch sehr
 * unterschiedlich sind. Der Katalog folgt dem üblichen Leistungsbild eines
 * Brandschutz-Ingenieurbüros vom Ortstermin bis zur Abnahmebegleitung.
 */
export const TimeActivity = z.enum([
    'ortsbegehung',
    'bestandsaufnahme',
    'konzepterstellung',
    'nachweisfuehrung',
    'plankontrolle',
    'behoerdenabstimmung',
    'pruefsachverstaendige',
    'besprechung',
    'korrespondenz',
    'dokumentation',
    'abnahmebegleitung',
    'nacharbeit',
    'reisezeit',
    'sonstiges',
]);
export const TIME_ACTIVITY_META = {
    ortsbegehung: {
        label: 'Ortsbegehung',
        description: 'Vor-Ort-Termin am Objekt: Begehung, Aufmaß, Fotodokumentation.',
        billableByDefault: true,
    },
    bestandsaufnahme: {
        label: 'Bestandsaufnahme',
        description: 'Auswertung von Bestandsunterlagen, Plänen und Vorgutachten.',
        billableByDefault: true,
    },
    konzepterstellung: {
        label: 'Konzepterstellung',
        description: 'Erarbeitung des Brandschutzkonzepts inkl. Schutzzielbetrachtung.',
        billableByDefault: true,
    },
    nachweisfuehrung: {
        label: 'Nachweisführung',
        description: 'Brandschutznachweis, rechnerische Nachweise, Abweichungsbegründungen.',
        billableByDefault: true,
    },
    plankontrolle: {
        label: 'Plankontrolle',
        description: 'Prüfung von Architekten-, TGA- und Flucht-/Rettungswegplänen.',
        billableByDefault: true,
    },
    behoerdenabstimmung: {
        label: 'Behördenabstimmung',
        description: 'Abstimmung mit Bauaufsicht, Brandschutzdienststelle und Feuerwehr.',
        billableByDefault: true,
    },
    pruefsachverstaendige: {
        label: 'Abstimmung Prüfsachverständige',
        description: 'Abstimmung mit Prüfsachverständigen, Bearbeitung von Prüfberichten.',
        billableByDefault: true,
    },
    besprechung: {
        label: 'Besprechung',
        description: 'Projektbesprechung, Jour fixe, Bauherrengespräch (auch online).',
        billableByDefault: true,
    },
    korrespondenz: {
        label: 'Korrespondenz',
        description: 'Schriftverkehr und Telefonate außerhalb fester Besprechungen.',
        billableByDefault: true,
    },
    dokumentation: {
        label: 'Dokumentation',
        description: 'Berichte, Protokolle, Zusammenstellung der Projektunterlagen.',
        billableByDefault: true,
    },
    abnahmebegleitung: {
        label: 'Abnahmebegleitung',
        description: 'Begleitung von Bauzustands-, Abnahme- und Wirk-Prinzip-Prüfungen.',
        billableByDefault: true,
    },
    nacharbeit: {
        label: 'Nacharbeit / Mängel',
        description: 'Korrektur eigener Fehler und Nachbesserung bereits gelieferter Leistungen. ' +
            'Standardmäßig NICHT abrechenbar — der Aufwand soll sichtbar bleiben, ' +
            'ohne beim Kunden zu landen.',
        billableByDefault: false,
    },
    reisezeit: {
        label: 'Reisezeit',
        description: 'An- und Abfahrt zum Objekt. Standardmäßig NICHT abrechenbar, weil ' +
            'Fahrtzeit üblicherweise über die Anfahrtspauschale abgegolten ist — ' +
            'je Eintrag umschaltbar.',
        billableByDefault: false,
    },
    sonstiges: {
        label: 'Sonstiges',
        description: 'Aufwand, der in keine der übrigen Positionen passt.',
        billableByDefault: true,
    },
};
/** Anzeigereihenfolge in Auswahllisten und Diagrammen (Projektverlauf). */
export const TIME_ACTIVITY_ORDER = [
    'ortsbegehung',
    'bestandsaufnahme',
    'konzepterstellung',
    'nachweisfuehrung',
    'plankontrolle',
    'behoerdenabstimmung',
    'pruefsachverstaendige',
    'besprechung',
    'korrespondenz',
    'dokumentation',
    'abnahmebegleitung',
    'nacharbeit',
    'reisezeit',
    'sonstiges',
];
export function timeActivityLabel(activity) {
    if (!activity)
        return 'Ohne Position';
    const parsed = TimeActivity.safeParse(activity);
    // Altbestand mit Freitext-Kategorie: unverändert anzeigen statt zu verschlucken.
    return parsed.success ? TIME_ACTIVITY_META[parsed.data].label : activity;
}
// ============================================================
// Zeiteinträge
// ============================================================
export const TimeEntryStatus = z.enum(['open', 'billed', 'cancelled']);
/**
 * Herkunft des im Eintrag festgeschriebenen Stundensatzes — reine
 * Nachvollziehbarkeit („warum stehen hier 95 €?").
 */
export const TimeRateSource = z.enum(['manual', 'project', 'default', 'none']);
/**
 * Geldbetrag in EUR mit maximal zwei Nachkommastellen.
 *
 * Die Nachkommastellen werden über die Cent-Ganzzahl geprüft statt über
 * `multipleOf(0.01)` — letzteres rechnet in Fließkomma und lehnt je nach Wert
 * legitime Beträge ab.
 */
const money = z
    .number()
    .nonnegative()
    .max(9_999.99)
    .refine((v) => Math.abs(v * 100 - Math.round(v * 100)) < 1e-6, {
    message: 'Höchstens zwei Nachkommastellen.',
});
export const timeEntry = z.object({
    id: z.number().int().positive(),
    projectId: z.number().int().positive(),
    userId: z.number().int().positive(),
    /** Anzeigename des Erfassers — für Filter und Auswertung ohne Zweitabfrage. */
    userName: z.string().nullable(),
    startedAt: z.string().datetime(),
    endedAt: z.string().datetime().nullable(),
    hours: z.number().nullable(),
    hourlyRate: z.number().nullable(),
    rateSource: TimeRateSource,
    billable: z.boolean(),
    description: z.string().nullable(),
    /** Position/Leistungsart. Altbestand kann Freitext enthalten. */
    category: z.string().nullable(),
    status: TimeEntryStatus,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * Erfassungsarten. Der Client schickt Wanduhrzeit + Datum als ISO-Instant
 * (`startedAt`/`endedAt`), damit die Zeitzone genau einmal — im Browser des
 * Erfassers — aufgelöst wird und der Server keine TZ-Annahme treffen muss.
 *
 *   (a) Start + Ende  → `endedAt` gesetzt, `hours` wird serverseitig berechnet
 *   (b) Dauer         → `hours` gesetzt, `endedAt` bleibt leer
 *
 * Genau eine der beiden Angaben ist Pflicht; beides zugleich ist erlaubt
 * (dann gewinnt das Intervall, siehe `resolveDuration`), keine von beiden
 * nicht — ein Eintrag ohne Dauer wäre für Auswertung und Rechnung wertlos.
 */
export const createTimeEntryRequest = z
    .object({
    startedAt: z.string().datetime(),
    endedAt: z.string().datetime().optional(),
    hours: z.number().positive().max(24).optional(),
    /**
     * Manuell übersteuerter Stundensatz. Fehlt er, löst der Server ihn über
     * Projekt-Satz → Admin-Vorgabe auf und schreibt ihn im Eintrag fest.
     */
    hourlyRate: money.optional(),
    billable: z.boolean().optional(),
    description: z.string().max(500).optional(),
    category: TimeActivity.optional(),
})
    .strict()
    .refine((v) => v.endedAt != null || v.hours != null, {
    message: 'Entweder Endzeit oder Dauer angeben.',
    path: ['hours'],
});
export const updateTimeEntryRequest = z
    .object({
    startedAt: z.string().datetime().optional(),
    endedAt: z.string().datetime().nullable().optional(),
    hours: z.number().positive().max(24).optional(),
    hourlyRate: money.nullable().optional(),
    billable: z.boolean().optional(),
    description: z.string().max(500).optional(),
    category: TimeActivity.optional(),
    status: TimeEntryStatus.optional(),
})
    .strict();
/** Aggregat je Position bzw. je Person — Datenbasis der Diagramme. */
export const timeBucket = z.object({
    key: z.string(),
    label: z.string(),
    hours: z.number(),
    /** Nur abrechenbare Stunden — die Differenz zu `hours` ist der interne Anteil. */
    billableHours: z.number(),
    amount: z.number(),
});
export const timeEntriesResponse = z.object({
    items: z.array(timeEntry),
    totalHours: z.number(),
    billableHours: z.number(),
    totalAmount: z.number().nullable(),
    byActivity: z.array(timeBucket),
    byUser: z.array(timeBucket),
});
/**
 * Antwort auf Anlegen/Ändern: Eintrag plus nicht-blockierende Hinweise
 * (z. B. Überschneidung mit einem anderen Eintrag am selben Tag).
 *
 * WARUM nur Warnung: Nacherfassung ist Alltag — wer eine Besprechung
 * nachträgt, die parallel zur Fahrtzeit lief, soll nicht ausgesperrt werden.
 */
export const timeEntryMutationResponse = z.object({
    entry: timeEntry,
    warnings: z.array(z.string()),
});
/** Filter der Listen-/Export-Route. Alle Felder optional. */
export const timeEntryFilter = z
    .object({
    category: TimeActivity.optional(),
    userId: z.coerce.number().int().positive().optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    status: TimeEntryStatus.optional(),
    // Aus dem Query-String kommt ein String — `z.coerce.boolean()` wäre hier
    // falsch, weil es "false" zu `true` macht (nicht-leerer String).
    billable: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
})
    .strict();
// ============================================================
// Stundensätze
// ============================================================
/**
 * Stundensätze werden **je Position** geführt, nicht je Rolle.
 *
 * Begründung: die Position bildet das Leistungsbild ab und trägt damit den
 * kaufmännischen Unterschied (Nachweisführung ist teurer als Korrespondenz).
 * Die Rollen des Systems (`gast`/`ersteller`/`freigeber`/`admin`) sind
 * Zugriffsrollen, keine Abrechnungsqualifikationen — ein Satz je Rolle würde
 * einen Zugriffsbegriff kaufmännisch umdeuten und zusätzlich eine 4×14-Matrix
 * erzeugen, die niemand pflegt. Wird später nach Qualifikation differenziert,
 * gehört dafür ein eigenes Stammdatenfeld am Benutzer angelegt; die
 * Auflösungskette unten bliebe unverändert.
 */
export const timeRate = z.object({
    category: TimeActivity,
    label: z.string(),
    /** EUR pro Stunde. `null` = kein Satz hinterlegt. */
    rate: z.number().nullable(),
    updatedAt: z.string().datetime().nullable(),
});
export const timeRateDefaultsResponse = z.object({
    items: z.array(timeRate),
    /** Ob der aufrufende Benutzer die Vorgaben ändern darf (nur Admin). */
    canEdit: z.boolean(),
});
export const projectTimeRate = timeRate.extend({
    /** Projektspezifischer Satz — `null`, wenn die Vorgabe gilt. */
    overrideRate: z.number().nullable(),
    /** Admin-Vorgabe, zur Anzeige „Vorgabe: 95 €". */
    defaultRate: z.number().nullable(),
});
export const projectTimeRatesResponse = z.object({
    items: z.array(projectTimeRate),
    /** Ob der aufrufende Benutzer Projekt-Sätze ändern darf (Projektleiter/Admin). */
    canEdit: z.boolean(),
});
/**
 * Setzen von Sätzen — `rate: null` löscht den Eintrag (Projekt fällt damit
 * auf die Admin-Vorgabe zurück).
 */
export const updateTimeRatesRequest = z
    .object({
    rates: z
        .array(z.object({ category: TimeActivity, rate: money.nullable() }))
        .min(1)
        .max(TIME_ACTIVITY_ORDER.length),
})
    .strict();
// ============================================================
// Reine Berechnung (Backend + Frontend teilen sich dieselbe Logik)
// ============================================================
/** Rundet auf zwei Nachkommastellen (kaufmännisch). */
export function round2(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}
/**
 * Dauer aus Start/Ende. Ergebnis in Stunden mit zwei Nachkommastellen —
 * das ist die Granularität, in der auch die Datenbank speichert.
 */
export function hoursBetween(startedAt, endedAt) {
    return round2((endedAt.getTime() - startedAt.getTime()) / 3_600_000);
}
/** Stunden+Minuten → Dezimalstunden (Erfassungsart „Dauer"). */
export function hoursFromHm(hours, minutes) {
    return round2(hours + minutes / 60);
}
/** Dezimalstunden → Stunden+Minuten für die Anzeige. */
export function hmFromHours(value) {
    const totalMinutes = Math.round(value * 60);
    return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
}
/** „3,25" → „3 h 15 min". */
export function formatHours(value) {
    if (value == null)
        return '—';
    const { hours, minutes } = hmFromHours(value);
    if (minutes === 0)
        return `${hours} h`;
    return `${hours} h ${String(minutes).padStart(2, '0')} min`;
}
/**
 * Bestimmt die verbindliche Dauer eines Eintrags.
 *
 * Sind Intervall UND Dauer angegeben, gewinnt das Intervall: Start- und
 * Endzeit sind die belegbare Angabe, die Dauer ist daraus ableitbar. Andernfalls
 * stünden im selben Datensatz zwei widersprüchliche Wahrheiten.
 */
export function resolveDuration(input) {
    if (input.endedAt) {
        const hours = hoursBetween(input.startedAt, input.endedAt);
        if (hours <= 0) {
            throw new RangeError('Die Endzeit muss nach der Startzeit liegen.');
        }
        return { hours, endedAt: input.endedAt };
    }
    if (input.hours != null && input.hours > 0) {
        return { hours: round2(input.hours), endedAt: null };
    }
    throw new RangeError('Entweder Endzeit oder Dauer angeben.');
}
export function findOverlaps(candidate, existing) {
    if (!candidate.endedAt)
        return [];
    const from = candidate.startedAt.getTime();
    const to = candidate.endedAt.getTime();
    return existing.filter((e) => {
        if (!e.endedAt)
            return false;
        return e.startedAt.getTime() < to && e.endedAt.getTime() > from;
    });
}
/**
 * Betrag eines Eintrags in Cent — Ganzzahl-Arithmetik, damit sich
 * Rundungsfehler nicht über die Summe einer Rechnung aufaddieren.
 */
export function amountCents(hours, rate, billable) {
    if (!billable || hours == null || rate == null)
        return 0;
    return Math.round(hours * Math.round(rate * 100));
}
export function centsToEuro(cents) {
    return round2(cents / 100);
}
/**
 * Auflösung des Stundensatzes: Projekt schlägt Vorgabe.
 *
 * Der aufgelöste Satz wird beim Erfassen im Eintrag festgeschrieben
 * (`TimeEntry.hourlyRate`) — siehe Kommentar am Backend-Handler.
 */
export function resolveHourlyRate(input) {
    if (input.manual != null)
        return { rate: input.manual, source: 'manual' };
    if (input.projectRate != null)
        return { rate: input.projectRate, source: 'project' };
    if (input.defaultRate != null)
        return { rate: input.defaultRate, source: 'default' };
    return { rate: null, source: 'none' };
}
// ============================================================
// Feature-Flags
// ============================================================
export const featureFlag = z.object({
    key: z.string(),
    enabled: z.boolean(),
    config: z.record(z.unknown()).nullable(),
    updatedAt: z.string().datetime(),
    updatedBy: z.number().int().positive().nullable(),
});
export const updateFeatureFlagRequest = z.object({
    enabled: z.boolean(),
    config: z.record(z.unknown()).optional(),
});
/** Kanonische Feature-Flag-Keys. */
export const FEATURE_FLAGS = {
    TIME_TRACKING: 'timeTrackingEnabled',
    /**
     * Sprachbedienung (Diktat + globale Sprachsteuerung). Standardmäßig AUS:
     * die Erkennung schaltet ein Mikrofon ein und überträgt die Aufnahme an den
     * Spracherkennungsdienst des Browsers — das schaltet der Betreiber bewusst
     * frei, statt es mit einer Auslieferung zu bekommen.
     */
    VOICE_CONTROL: 'voiceControlEnabled',
};
